import type { StepDetailProps } from "@/types/crm";
import React, { memo } from "react";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";
import { MoreVertical, Trash2, Trophy, XCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import type { OpportunityStatus } from "@/hooks/useOpportunities";

interface ExtendedStepDetailProps extends StepDetailProps {
  status?: OpportunityStatus;
  onDelete?: () => void;
  isArchived?: boolean;
}

const StepDetail: React.FC<ExtendedStepDetailProps> = memo(({
  contactId,
  contactName,
  tags,
  contactTags,
  productName,
  amount,
  createdAt,
  attributedUserName,
  status,
  onDelete,
  isArchived
}) => {
  const linkTo = contactId ? `/crm/${contactId}` : '/crm';

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const getStatusIndicator = () => {
    if (!isArchived) return null;
    if (status === 'won') {
      return (
        <div className="absolute top-2 right-2">
          <Trophy className="w-4 h-4 text-green-500" />
        </div>
      );
    }
    if (status === 'lost') {
      return (
        <div className="absolute top-2 right-2">
          <XCircle className="w-4 h-4 text-red-500" />
        </div>
      );
    }
    return null;
  };

  const getBorderStyle = () => {
    if (!isArchived) return 'border-[#3F3F464D]';
    if (status === 'won') return 'border-green-600/50';
    if (status === 'lost') return 'border-red-600/50';
    return 'border-[#3F3F464D]';
  };

  return (
    <div className={`relative rounded-xl border ${getBorderStyle()} bg-neutral-600 px-3 py-4 flex flex-col gap-[10px] text-neutral-50 w-full`}>
      {getStatusIndicator()}
      <div className="flex justify-between items-center gap-2">
        <Link
          to={linkTo}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 overflow-hidden"
        >
          <div className="flex items-center gap-2">
            <span
              className="text-base font-semibold truncate flex-1 min-w-0"
              title={contactName}
            >
              {contactName}
            </span>
            <img src="/link.svg" alt="Contact Icon" className="flex-shrink-0" />
          </div>
        </Link>
        {onDelete && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-neutral-500"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-40 p-1 bg-neutral-700 border-neutral-600"
              align="end"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-neutral-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 border-b border-[#3F3F464D] py-2">
          {productName && (
            <div className="flex items-center gap-2">
              <img src="/buy.svg" alt="Coin Icon" />
              <span className="text-sm">{productName}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <img src="/coin.svg" alt="Coin Icon" />
            <span className="text-sm">{amount}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0">
            {getInitials(attributedUserName)}
          </div>
          <span className="text-sm truncate">
            {attributedUserName || (
              <span className="text-neutral-400">Nao atribuido</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <img src="/date.svg" alt="" />
          <span className="text-sm">{createdAt}</span>
        </div>
        {contactTags && contactTags.length > 0 && (
          <div className="flex items-start gap-2">
            <img src="/bookmark.svg" alt="" className="mt-1 flex-shrink-0" />
            <div className="flex gap-1 flex-wrap max-h-[60px] overflow-hidden flex-1">
              {contactTags.slice(0, 3).map((tag, idx) => (
                <Badge className="capitalize px-2 py-0.5 text-xs bg-red-600 hover:bg-red-600 border-0 text-white" key={idx}>
                  {tag}
                </Badge>
              ))}
              {contactTags.length > 3 && (
                <Badge variant="outline" className="px-2 py-0.5 text-xs">
                  +{contactTags.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

StepDetail.displayName = 'StepDetail';

export default StepDetail;
